import React, { useEffect, useState, useCallback } from "react"
import Select from "react-select"
import axios from "axios"
import Swal from "sweetalert2"
import configure from "configure"
import Loader from "pages/Separate/Loader"

// ── Alert type definitions ────────────────────────────────────────────────────
const ALERT_TYPES = [
  {
    key: "ai_failure",
    label: "AI Service Failure",
    description:
      "Triggered when the AI API is unreachable or returns an error (e.g. invalid key, quota exceeded).",
    icon: "fas fa-robot",
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  {
    key: "email_failure",
    label: "Email Service Failure",
    description:
      "Triggered when the system cannot send emails (SMTP error, authentication failure, etc.).",
    icon: "fas fa-envelope-open-text",
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  {
    key: "onedrive_failure",
    label: "OneDrive Upload Failure",
    description:
      "Triggered when a file upload to OneDrive fails (token expiry, quota, network issues).",
    icon: "fas fa-cloud-upload-alt",
    color: "#0369a1",
    bg: "#f0f9ff",
    border: "#bae6fd",
  },
  // {
  //   key: "cron_failure",
  //   label: "Cron Job Failure",
  //   description: "Triggered when a scheduled task (recurring meetings, reminders, punching cron) crashes.",
  //   icon: "fas fa-clock",
  //   color: "#b91c1c",
  //   bg: "#fff1f2",
  //   border: "#fecdd3",
  // },
  {
    key: "db_failure",
    label: "Database Connection Error",
    description:
      "Triggered when the system cannot reach the database or a query throws a critical error.",
    icon: "fas fa-database",
    color: "#065f46",
    bg: "#f0fdf4",
    border: "#bbf7d0",
  },
  // {
  //   key: "notification_failure",
  //   label: "Push Notification Failure",
  //   description: "Triggered when push notifications cannot be delivered to users.",
  //   icon: "fas fa-bell-slash",
  //   color: "#9d174d",
  //   bg: "#fdf2f8",
  //   border: "#f9a8d4",
  // },
]

// ── Small toggle switch ───────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <div className="form-check form-switch mb-0">
    <input
      className="form-check-input"
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      style={{ width: 40, height: 22, cursor: "pointer" }}
    />
  </div>
)

// ── Main component ────────────────────────────────────────────────────────────
const AlertSettingsIndex = () => {
  const [loading, setLoading] = useState(false)
  const [staffOptions, setStaffOptions] = useState([])

  // State: { [alert_type]: { user_ids: string|null, is_active: 0|1, id: number|null, dirty: bool } }
  const [settings, setSettings] = useState(() =>
    Object.fromEntries(
      ALERT_TYPES.map(a => [
        a.key,
        { user_ids: null, is_active: 1, id: null, dirty: false },
      ])
    )
  )

  // ── Fetch staff ─────────────────────────────────────────────────────────────
  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${configure.nodeApi}/general_staff`, {
        headers: { Accept: "application/json" },
      })
      if (res.data.success) {
        setStaffOptions(
          (res.data.data || []).map(u => ({ value: u.id, label: u.name }))
        )
      }
    } catch (e) {
      console.warn("staff fetch error", e)
    }
  }, [])

  // ── Fetch saved alert settings ──────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(
        `${configure.nodeApi}/alert-settings/getAll`,
        {
          headers: { Accept: "application/json" },
        }
      )
      if (res.data.success) {
        const saved = res.data.data || []
        setSettings(prev => {
          const next = { ...prev }
          saved.forEach(row => {
            if (next[row.alert_type] !== undefined) {
              next[row.alert_type] = {
                ...next[row.alert_type],
                id: row.id,
                user_ids: row.user_ids || null,
                is_active: row.is_active,
                dirty: false,
              }
            }
          })
          return next
        })
      }
    } catch (e) {
      console.warn("alert settings fetch error", e)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchStaff(), fetchSettings()]).finally(() =>
      setLoading(false)
    )
  }, [fetchStaff, fetchSettings])

  // ── Convert stored user_ids string → react-select value array ──────────────
  const toSelectValue = user_ids => {
    if (!user_ids) return []
    return String(user_ids)
      .split(",")
      .map(s => s.trim())
      .filter(Boolean)
      .map(id => staffOptions.find(o => String(o.value) === id))
      .filter(Boolean)
  }

  // ── Handle user selection change ────────────────────────────────────────────
  const handleUsersChange = (alertKey, selected) => {
    const ids = (selected || []).map(o => o.value).join(",")
    setSettings(prev => ({
      ...prev,
      [alertKey]: { ...prev[alertKey], user_ids: ids || null, dirty: true },
    }))
  }

  // ── Handle toggle change ────────────────────────────────────────────────────
  const handleToggle = (alertKey, val) => {
    setSettings(prev => ({
      ...prev,
      [alertKey]: { ...prev[alertKey], is_active: val ? 1 : 0, dirty: true },
    }))
  }

  // ── Save single alert row ───────────────────────────────────────────────────
  const handleSave = async alertKey => {
    const row = settings[alertKey]
    setLoading(true)
    try {
      const res = await axios.post(
        `${configure.nodeApi}/alert-settings/save`,
        {
          alert_type: alertKey,
          user_ids: row.user_ids || null,
          is_active: row.is_active,
        },
        { headers: { Accept: "application/json" } }
      )
      if (res.data.success) {
        setSettings(prev => ({
          ...prev,
          [alertKey]: {
            ...prev[alertKey],
            id: res.data.data?.id || prev[alertKey].id,
            dirty: false,
          },
        }))
        Swal.fire({
          icon: "success",
          title: "Saved!",
          timer: 1200,
          showConfirmButton: false,
        })
      } else {
        Swal.fire({ icon: "error", title: res.data.message || "Save failed" })
      }
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: e.message,
      })
    } finally {
      setLoading(false)
    }
  }

  // ── Save all ────────────────────────────────────────────────────────────────
  const handleSaveAll = async () => {
    setLoading(true)
    try {
      await Promise.all(
        ALERT_TYPES.map(a =>
          axios.post(
            `${configure.nodeApi}/alert-settings/save`,
            {
              alert_type: a.key,
              user_ids: settings[a.key].user_ids || null,
              is_active: settings[a.key].is_active,
            },
            { headers: { Accept: "application/json" } }
          )
        )
      )
      setSettings(prev => {
        const next = { ...prev }
        ALERT_TYPES.forEach(a => {
          next[a.key] = { ...next[a.key], dirty: false }
        })
        return next
      })
      Swal.fire({
        icon: "success",
        title: "All alert settings saved!",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: e.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const dirtyCount = ALERT_TYPES.filter(a => settings[a.key]?.dirty).length

  return (
    <>
      {loading && <Loader />}
      <div className="p-4 bg-white shadow">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-3">
          <div>
            <h5
              style={{
                fontWeight: 700,
                color: "#1f2937",
                fontSize: 17,
                marginBottom: 4,
              }}
            >
              Alert Settings
            </h5>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              Choose who receives email &amp; push notifications when a system
              failure occurs. Each alert type can have its own set of
              responsible persons.
            </p>
          </div>
          {dirtyCount > 0 && (
            <button className="btn btn-save btn-label" onClick={handleSaveAll}>
              <i className="fas fa-save me-2 label-icon"></i>
              Save All ({dirtyCount} changed)
            </button>
          )}
        </div>

        {/* Alert cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {ALERT_TYPES.map(alert => {
            const row = settings[alert.key]
            const selectedUsers = toSelectValue(row.user_ids)

            return (
              <div
                key={alert.key}
                style={{
                  border: `1.5px solid ${row.dirty ? "#c89ee0" : alert.border}`,
                  borderLeft: `4px solid ${
                    row.is_active ? alert.color : "#d1d5db"
                  }`,
                  borderRadius: 10,
                  padding: "18px 20px",
                  background: row.is_active ? alert.bg : "#fafafa",
                  transition: "border-color .15s, background .15s",
                  opacity: row.is_active ? 1 : 0.7,
                }}
              >
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  {/* Icon */}
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: row.is_active ? alert.color : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "background .15s",
                    }}
                  >
                    <i
                      className={`${alert.icon} text-white`}
                      style={{ fontSize: 17 }}
                    ></i>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#1f2937",
                        }}
                      >
                        {alert.label}
                      </span>
                      {row.dirty && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 20,
                            background: "#fef3c7",
                            color: "#92400e",
                          }}
                        >
                          Unsaved
                        </span>
                      )}
                      {!row.is_active && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 20,
                            background: "#f3f4f6",
                            color: "#6b7280",
                          }}
                        >
                          Disabled
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 14,
                        lineHeight: 1.5,
                      }}
                    >
                      {alert.description}
                    </p>

                    {/* User select */}
                    <div style={{ maxWidth: 560 }}>
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#4b5563",
                          marginBottom: 5,
                          display: "block",
                        }}
                      >
                        <i
                          className="fas fa-users me-1"
                          style={{ color: alert.color }}
                        ></i>
                        Alert Recipients
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#9ca3af",
                            marginLeft: 6,
                          }}
                        >
                          (leave empty to send to nobody)
                        </span>
                      </label>
                      <Select
                        isMulti
                        placeholder="Select users to notify…"
                        options={staffOptions}
                        value={selectedUsers}
                        onChange={val => handleUsersChange(alert.key, val)}
                        isDisabled={!row.is_active}
                        classNamePrefix="select"
                        styles={{
                          control: base => ({
                            ...base,
                            fontSize: 13,
                            borderColor: "#e5e7eb",
                            minHeight: 36,
                          }),
                          menu: base => ({
                            ...base,
                            fontSize: 13,
                            zIndex: 9999,
                          }),
                        }}
                      />
                    </div>
                  </div>

                  {/* Right controls */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 15,
                      flexShrink: 0,
                    }}
                  >
                    {/* Toggle */}
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: 11, color: "#6b7280" }}>
                        {row.is_active ? "Active" : "Inactive"}
                      </span>
                      <Toggle
                        checked={!!row.is_active}
                        onChange={val => handleToggle(alert.key, val)}
                      />
                    </div>

                    {/* Save button */}
                    <button
                      className="btn btn-sm btn-save btn-label"
                      onClick={() => handleSave(alert.key)}
                    >
                      <i className="fas fa-save me-1 label-icon"></i>
                      Save
                    </button>
                  </div>
                </div>

                {/* Recipient count badge */}
                {selectedUsers.length > 0 && (
                  <div
                    className="d-flex flex-wrap gap-1 mt-3"
                    style={{ paddingLeft: 58 }}
                  >
                    {selectedUsers.map(u => (
                      <span
                        key={u.value}
                        style={{
                          fontSize: 11,
                          padding: "3px 10px",
                          borderRadius: 20,
                          background: "#fff",
                          border: `1px solid ${alert.border}`,
                          color: alert.color,
                          fontWeight: 500,
                        }}
                      >
                        <i
                          className="fas fa-user me-1"
                          style={{ fontSize: 9 }}
                        ></i>
                        {u.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Footer summary */}
        <div
          className="d-flex align-items-center gap-3 mt-4 pt-3 flex-wrap"
          style={{
            borderTop: "1px solid #f3f4f6",
            fontSize: 12,
            color: "#9ca3af",
          }}
        >
          <span>
            <i className="fas fa-info-circle me-1"></i>
            Changes to individual rows are saved immediately via the row-level
            <strong className="ms-1">Save</strong> button, or use
            <strong className="ms-1">Save All</strong> when multiple rows are
            changed.
          </span>
          <span style={{ marginLeft: "auto" }}>
            {ALERT_TYPES.filter(a => settings[a.key]?.is_active).length} of{" "}
            {ALERT_TYPES.length} alerts enabled
          </span>
        </div>
      </div>
    </>
  )
}

export default AlertSettingsIndex
